import Keycloak from 'keycloak-js';
import Config from '../config/Config';

const { keycloakService } = Config;

// Create instance
const keycloak = new Keycloak({
  url: keycloakService.url,
  realm: keycloakService.realm,
  clientId: keycloakService.clientId,
});

// Initialize Keycloak
let isInitialized = false;

export const initKeycloak = async () => {
  if (isInitialized) return true; // ignore additional calls

  try {
    const authenticated = await keycloak.init({ onLoad: 'login-required' });
    isInitialized = true;
    return authenticated;
  } catch (error) {
    console.error('Keycloak initialization error:', error);
    throw error;
  }
};

// Simple safe-access helper
const parsed = () => keycloak.tokenParsed || {};

// Demo mode: KeycloakContext registers a real refresh handler here so that
// the axios interceptor in DppManagerService can trigger an actual token
// refresh instead of being a no-op.
let _demoRefreshHandler = null;
let _refreshingPromise = null;

export const setDemoRefreshHandler = (fn) => {
  _demoRefreshHandler = fn;
};

// Token helpers
export const getToken = () => keycloak.token;
export const getTokenParsed = () => parsed();
export const refreshToken = async () => {
  if (Config.isDemo === 'true') {
    if (_demoRefreshHandler) {
      // Deduplicate concurrent refresh calls — all callers await the same promise.
      if (!_refreshingPromise) {
        _refreshingPromise = _demoRefreshHandler().finally(() => {
          _refreshingPromise = null;
        });
      }
      await _refreshingPromise;
    }
    return true;
  }
  return keycloak.updateToken(keycloakService.tokenRefreshInterval);
};
export const isTokenExpired = () => keycloak.isTokenExpired();
export const isAuthenticated = () => keycloak.authenticated;

// User info helpers
export const getUsername = () => parsed().preferred_username;
export const getEmail = () => parsed().email;
export const isEmailVerified = () => parsed().email_verified;
export const getTimeTokenExpires = () => parsed().exp;
export const getTimeAuthenticated = () => {
  const p = parsed();
  return p.auth_time || p.iat;
};

// Role helpers
export const getRealmRoles = () => parsed().realm_access?.roles || [];
export const getBasyxRoles = () =>
  parsed().resource_access?.['basyx-client-api']?.roles || [];

export const hasRole = (role) =>
  getRealmRoles().includes(role) || getBasyxRoles().includes(role);

// Object export for easier consumption
export const KeycloakService = {
  keycloak,
  initKeycloak,
  login: () => keycloak.login(),
  logout: () => keycloak.logout(),
  getToken,
  getTokenParsed,
  setDemoRefreshHandler,
  refreshToken,
  isTokenExpired,
  isAuthenticated,
  getUsername,
  getEmail,
  isEmailVerified,
  getTimeTokenExpires,
  getTimeAuthenticated,
  getRealmRoles,
  getBasyxRoles,
  hasRole,
};
